import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import styles from './PageSearch.module.scss';
import coveoSearch from '../../apis/coveo';
import coveoUaSearch from '../../apis/coveoua';
import { SearchInputUI } from '../SearchInput';
import ElementLink from '../ElementLink';
import Asset from '../Asset';
import { SearchControls } from './SearchControls';
import {
  FilterCard,
  reduceFilters,
  getInitialFilters,
  translateFiltersToQuery,
  isFilterOptionSelected
} from './SearchFilters';
import { getCommonString } from '../../utils/i18n';
import Breadcrumb from '../Breadcrumb';
import { setLocaleToApi } from '../../utils/localeUtils'
// These are pretty much straight from Coveo, except `type`.
export const SearchResultItemPropTypes = {
  title: PropTypes.string.isRequired,
  uri: PropTypes.string.isRequired,
  icon: PropTypes.string,
  excerpt: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['topic', 'video', 'liveSession', 'selfGuided', 'unknown']).isRequired
};

function SearchResultItem({ title, uri, icon, excerpt, type, permanentid, searchUid, vistorid, image }) {
  const { t } = useTranslation();
  return (
    <li className={styles.searchResult}>
      <div className={styles.searchResultContainer}>
        <div className={styles.searchResultIcon}>
          <p className={styles.searchResultType}>
            {type === "video" && <span className={styles.videoIcon}></span>}
            {type !== 'unknown' ? getCommonString(`searchResultType-${type}`, t) : ''}
          </p>
        </div>
        <div className={styles.searchResultText}>
          <ElementLink
            className={styles.searchResultLink}
            href={uri}
            isModal={false}
            download={false}
            linkText={title}
            trackingId={`page_search_${new URL(uri).pathname}`}
            data-testid="Search-LinkTitle"
            permanentid={permanentid}
            searchUid={searchUid}
            vistorid={vistorid}
          />
          <p className={styles.searchResultExcerpt}>{excerpt}</p>

        </div>
      </div>
      {image && <img src={image} alt="thumbnailurl" width={178} height={100} className={styles.thumbnailImage} />}
    </li>
  );
}

SearchResultItem.propTypes = SearchResultItemPropTypes;
SearchResultItem.defaultProps = {
  icon: undefined
};

export const SearchResultsPropTypes = {
  results: PropTypes.arrayOf(PropTypes.shape(SearchResultItemPropTypes)).isRequired
};

function SearchResults({ results, searchUid, vistorid }) {
  return (
    <ul className={styles.searchResultList}>
      {results.map((result) => (
        <SearchResultItem
          key={result.uri}
          title={result.title}
          uri={result.uri}
          icon={result.icon}
          excerpt={result.excerpt}
          type={result.type}
          permanentid={result.raw.permanentid}
          searchUid={searchUid}
          vistorid={vistorid}
          image={result.raw?.learnthumbnailurl}
        />
      ))}
    </ul>
  );
}

SearchResults.propTypes = SearchResultsPropTypes;

export const PageSearchPropTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  searchImages: PropTypes.object.isRequired
};

const applyResultType = (result, searchImages) => {
  const detectors = {
    topic: /\/topics\/?/,
    video: /\/videos\/?/,
    video: /\/video-library\/?/,
    selfGuided: /\/self-guided-learning\/?/
  };
  const ret = { ...result };
  ret.type = 'unknown';
  ret.icon = searchImages.defaultImage;
  Object.entries(detectors).forEach((matcher) => {
    const [type, regex] = matcher;
    if (ret.uri.match(regex)) {
      const totalLength = ret.uri.split("/")
      const index = totalLength.indexOf("self-guided-learning");
      if (index && ((totalLength.length - 1) - index) === 1) {
        ret.type = type;
      }
      else if (index && ((totalLength.length - 1) - index) === 2) {
        ret.type = "Topic";
      }
      else {
        ret.type = type;
      }

      ret.icon = searchImages[`${type}Image`];
    }
  });

  return ret;
};

const runQuery = async (
  text,
  queryFilters,
  resultsPerPage,
  setNumResults,
  currentPage,
  setSearchUid,
  vistorid,
  searchEventName
) => {
  vistorid = localStorage.getItem('visitorId');
  const rawResponse = await coveoSearch(text, queryFilters, resultsPerPage, currentPage * resultsPerPage);
  const response = await rawResponse.json();
  const count = response.totalCount;
  setNumResults(() => count);
  setSearchUid(() => response.searchUid);
  coveoUaSearch(response.searchUid, text, vistorid, searchEventName);
  return response.results;
};

function PageSearch({ searchImages }) {
  const router = useRouter();
  const { t, lang } = useTranslation();

  const [results, setResults] = useState([]);
  const [resultsPerPage, setResultsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [numResults, setNumResults] = useState(0);
  const [searchUid, setSearchUid] = useState(0);
  const [vistorid, setVisitorid] = useState('');
  const [filters, setFilters] = useState(getInitialFilters(t, lang));
  const [showfilters, setShowFilters] = useState(false)
  const resultsRef = useRef(null);
  const pendingSearchEventRef = useRef('searchboxSubmit');

  const query = router.query.q;
  const [search, setSearch] = useState('');
  const [currentSearch, setCurrentSearch] = useState('');
  const [inputpagesearhBar, setInputPageSearchBar] = useState(true)

  const numPages = Math.ceil(numResults / resultsPerPage);

  const queryFilters = translateFiltersToQuery(filters);
  useEffect(() => {
    let fetchUserID = localStorage.getItem('visitorId');
    setVisitorid(fetchUserID);
  }, [vistorid]);
  useEffect(() => {
    if (router.query.q) {
      setSearch(router.query.q);
    }
  }, [router.query.q])

  const handleSearch = (text, searchEventName = 'searchboxSubmit') =>
    runQuery(
      text,
      queryFilters,
      resultsPerPage,
      setNumResults,
      currentPage,
      setSearchUid,
      vistorid,
      searchEventName
    )

      .then((res) => (
        res.map((result) => applyResultType(result, searchImages)))
      )
      .then(setResults)
      .then((result) => {
        setCurrentSearch(() => text);
        return result;
      })
      .catch(() => {
        setCurrentSearch(() => text);
        setResults([]);
      });
  const handleClearAllFilters = () => {
    setFilters(getInitialFilters(t, lang));
    pendingSearchEventRef.current = 'searchboxSubmit';
  };
  // next.js initially gives you a router with a blank query,
  // and then updates it to have the actual query on later renders.
  useEffect(() => {
    if (inputpagesearhBar && query && query.length) {
      handleSearch(query, 'searchboxSubmit');
      setSearch(() => query);
    }
  }, [query]);

  // // Redo the search if the user changes the filter
  useEffect(() => {
    if (queryFilters) {
      handleSearch(search, pendingSearchEventRef.current);
      pendingSearchEventRef.current = 'searchboxSubmit';
    }

  }, [queryFilters]);

  // Redo the search and scroll back up if results per page or current page change,
  // but we only want to do this post-first render.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (!isFirstRender.current) {
      handleSearch(search, 'searchboxSubmit').then(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      });
    }

    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, [resultsPerPage, currentPage]);

  const firstResult = currentPage * resultsPerPage + 1;
  const lastResult = Math.min(currentPage * resultsPerPage + resultsPerPage, numResults);

  return (
    <div data-testid="PageSearch">
      <div className={styles.breadcrumbContainer}>
        <Breadcrumb breadcrumbs={[{ text: t('common:searchBreadcrumb'), href: '/search' }]} />
      </div>

      <section className={styles.searchHero}>
        <div className={styles.pageHeading}>
          <h2 className={styles.pageHeader} ref={resultsRef}>
            {t('common:searchHeader')}
          </h2>
          <SearchInputUI size="lg" value={search} query={query} setValue={setSearch}
            onSearch={(value) => {
              handleSearch(value),
              setInputPageSearchBar(false)
            }} />
        </div>
      </section>
      {results.length > 0 && (
        <section>
          <div className={styles.pageContent}>
            <div className={styles.resultContent}>
              <div className={styles.filters}>
                {Object.entries(filters).map(([filterName, filterConfig]) => {
                  return (
                    <FilterCard
                      key={filterName}
                      filterName={filterName}
                      filterConfig={filterConfig}
                      onFilterSelected={async (filter, value, selected) => {
                        pendingSearchEventRef.current = isFilterOptionSelected(filters, filter, value, selected);
                        setFilters((prev) => {
                          return reduceFilters(prev, filter, value, selected);
                        });
                      }}
                    />
                  );
                })}
              </div>
              <section className={styles.results}>

                {!!results.length && (
                  <>
                    <div className={styles.countFilters}>
                      <div className={styles.searchResultStatus}>
                        {numResults} {t('common:searchResultStatus')}
                      </div>
                      {queryFilters ? <div className={styles.clearAllFilters} onClick={() => handleClearAllFilters()}>{t('common:clearall')}</div> : ""}
                      <button onClick={() => setShowFilters(true)} className={styles.filterButton}>{t('common:textFilterBy')}</button>
                    </div>

                    <SearchResults data-testid="SearchResults" results={results} searchUid={searchUid} vistorid={vistorid} />
                    <SearchControls
                      data-testid="SearchControls"
                      onPageSizeChange={setResultsPerPage}
                      currentPageSize={resultsPerPage}
                      onPageChange={setCurrentPage}
                      currentPage={currentPage}
                      numPages={numPages}
                    />
                  </>
                )}
              </section>
            </div>
          </div>
        </section>
      )}
      {showfilters && <div className={styles.mobilefilters}>
        <div className={styles.mobilefilterspage}>
          <div className={styles.filterHeading}><span>{t('common:filterstext')}</span><span onClick={() => setShowFilters(false)} className={styles.crossicon}></span></div>
          <div className={styles.filtercards}>
            {Object.entries(filters).map(([filterName, filterConfig]) => {
              return (
                <FilterCard
                  key={filterName}
                  filterName={filterName}
                  filterConfig={filterConfig}
                  onFilterSelected={async (filter, value, selected) => {
                    pendingSearchEventRef.current = isFilterOptionSelected(filters, filter, value, selected);
                    setFilters((prev) => {
                      return reduceFilters(prev, filter, value, selected);
                    });
                  }}
                />
              );
            })}
          </div>
          <div className={styles.reslutsCountConatiner}>
            <button onClick={() => handleClearAllFilters()}>{t('common:clearall')}</button>
            <button onClick={() => setShowFilters(false)}>{`${t('common:see')} ${numResults} ${t('common:results')}`}</button>

          </div>
        </div>
      </div>}
      {results.length === 0 && (
        <div className={styles.nosearchResultStatus}>
          <div className={styles.filters}>
            {Object.entries(filters).map(([filterName, filterConfig]) => {
              return (
                <FilterCard
                  key={filterName}
                  filterName={filterName}
                  filterConfig={filterConfig}
                  onFilterSelected={async (filter, value, selected) => {
                    pendingSearchEventRef.current = isFilterOptionSelected(filters, filter, value, selected);
                    setFilters((prev) => {
                      return reduceFilters(prev, filter, value, selected);
                    });
                  }}
                />
              );
            })}
          </div>
          <div>
            <div className={styles.countFilters}>
              <span className={styles.searchResultStatus}> 0 {t('common:searchResultStatus')}</span>
              <button onClick={() => setShowFilters(true)} className={styles.filterButton}>{t('common:textFilterBy')}</button>
            </div>
            <div className={styles.contentWrapper}>
              <span className={styles.noresults}></span>
              <div >
                <h2 className={styles.textContent}>{t("common:noData")}</h2>
                <p><span className={styles.resetFilters} onClick={() => handleClearAllFilters()}>{t('common:clearall')}</span>, {t('common:browseKeyword')} <a href="/" className={styles.learntext}>{t('common:learnText')}</a> {t('common:moreAnswers')}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

PageSearch.propTypes = PageSearchPropTypes;

export default PageSearch;